'use client';

import { media } from '@chewy/kib-utilities';
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';

import styles from './styles.module.scss';

import landingVideoDesktopPoster from '@/assets/landing-video-desktop.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import landingVideoDesktop from '@/assets/landing-video-desktop.mp4';
import landingVideoMobilePoster from '@/assets/landing-video-mobile.jpg';
// @ts-expect-error - MP4 imports are configured in next.config but TS doesn't recognize them
import landingVideoMobile from '@/assets/landing-video-mobile.mp4';

const HERO_MIN_MD = media.query('min-md');

const getVideoSrc = (isDesktop: boolean) => (isDesktop ? landingVideoDesktop : landingVideoMobile);

type HeroPosterProps = {
  className: string;
};

const HeroPoster = ({ className }: HeroPosterProps) => (
  <picture className={styles.heroVideoPosterPicture}>
    <source media={HERO_MIN_MD} srcSet={landingVideoDesktopPoster.src} />
    <img
      className={className}
      src={landingVideoMobilePoster.src}
      alt=""
      aria-hidden="true"
      data-a11y="decorative"
      decoding="sync"
      fetchPriority="high"
    />
  </picture>
);

export const LandingHeroVideo = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [showPoster, setShowPoster] = useState(true);

  useEffect(() => {
    const desktopQuery = window.matchMedia(media.query('min-md'));
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const syncVideo = () => {
      const reducedMotion = motionQuery.matches;

      setPrefersReducedMotion(reducedMotion);
      setShowPoster(true);
      setVideoSrc(reducedMotion ? null : getVideoSrc(desktopQuery.matches));
    };

    syncVideo();
    desktopQuery.addEventListener('change', syncVideo);
    motionQuery.addEventListener('change', syncVideo);

    return () => {
      desktopQuery.removeEventListener('change', syncVideo);
      motionQuery.removeEventListener('change', syncVideo);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;

    if (video.getAttribute('src') !== videoSrc) {
      video.src = videoSrc;

      try {
        video.load();
        const playPromise = video.play();
        if (playPromise !== undefined) {
          void playPromise.catch(() => {});
        }
      } catch {
        // jsdom and other test environments may not implement media playback APIs.
      }
    }
  }, [videoSrc]);

  if (prefersReducedMotion) {
    return <HeroPoster className={styles.heroVideo} />;
  }

  return (
    <div className={cx(styles.heroVideoWrap, showPoster && styles.heroVideoWrapWithPoster)}>
      <video
        ref={videoRef}
        className={styles.heroVideo}
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        onPlaying={() => setShowPoster(false)}
        aria-hidden="true"
        tabIndex={-1}
      />
      {showPoster && <HeroPoster className={styles.heroVideoPoster} />}
    </div>
  );
};
